import { FC, type ReactNode } from 'react'
import { Group, Kbd, Stack, Text, Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import checkeredCircle from '@images/checkeredCircle.png'
import {
  PLANNER_CONTINUATION_COLOR,
  PLANNER_GAP_FIX_COLOR,
  PLANNER_HINT_COLOR,
  PLANNER_MUTED_COLOR,
  PLANNER_NON_ROUTABLE_NODE_COLOR,
  PLANNER_NON_ROUTABLE_NODE_OPACITY,
  PLANNER_PARKING_FILL_COLOR,
  PLANNER_PARKING_FILL_OPACITY,
  PLANNER_PARKING_LINE_COLOR,
  PLANNER_PATH_COLOR,
  PLANNER_START_NODE_COLOR,
  PLANNER_STREET_COLOR,
} from './constants'

const PathSwatch: FC<{ color: string; dashed?: boolean }> = ({ color, dashed }) => (
  <svg width={28} height={10} aria-hidden viewBox="0 0 28 10" style={{ flexShrink: 0 }}>
    <line
      x1={0}
      y1={5}
      x2={28}
      y2={5}
      stroke={color}
      strokeWidth={4}
      strokeLinecap="round"
      strokeDasharray={dashed ? '6,4' : undefined}
    />
  </svg>
)

const AreaSwatch: FC<{ fill: string; fillOpacity: number; stroke: string }> = ({ fill, fillOpacity, stroke }) => (
  <svg width={22} height={16} aria-hidden viewBox="0 0 22 16" style={{ flexShrink: 0 }}>
    <rect
      x={1}
      y={1}
      width={20}
      height={14}
      rx={2}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={stroke}
      strokeWidth={1.5}
    />
  </svg>
)

const NodeSwatch: FC<{ color: string; label?: string; opacity?: number }> = ({ color, label, opacity = 1 }) => (
  <div
    style={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: color,
      opacity,
      border: '2px solid #fff',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      fontWeight: 600,
      color: '#212529',
      flexShrink: 0,
    }}
  >
    {label}
  </div>
)

const FinishNodeSwatch: FC = () => (
  <img
    src={checkeredCircle}
    alt=""
    width={22}
    height={22}
    style={{
      flexShrink: 0,
      display: 'block',
      borderRadius: '50%',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
    }}
  />
)

const LegendRow: FC<{ children: ReactNode }> = ({ children }) => (
  <Group gap="sm" wrap="nowrap" align="center">
    {children}
  </Group>
)

export const RoutePlannerHelp: FC = () => {
  const { t } = useTranslation('routePlanner')

  return (
    <Stack gap="lg">
      <Stack gap={6}>
        <Title order={5}>{t('helpSegmentsTitle')}</Title>
        <LegendRow>
          <PathSwatch color={PLANNER_STREET_COLOR} />
          <Text size="sm">{t('helpSegmentStreet')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_PATH_COLOR} />
          <Text size="sm">{t('helpSegmentPath')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_CONTINUATION_COLOR} dashed />
          <Text size="sm">{t('helpSegmentContinuation')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_MUTED_COLOR} dashed />
          <Text size="sm">{t('helpSegmentNonBillable')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_PATH_COLOR} dashed />
          <Text size="sm">{t('helpSegmentEditTail')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_MUTED_COLOR} dashed />
          <Text size="sm">{t('helpSegmentEditTailObsolete')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_HINT_COLOR} dashed />
          <Text size="sm">{t('helpSegmentShortestPath')}</Text>
        </LegendRow>
        <LegendRow>
          <PathSwatch color={PLANNER_GAP_FIX_COLOR} dashed />
          <Text size="sm">{t('helpSegmentGapFix')}</Text>
        </LegendRow>
        <LegendRow>
          <AreaSwatch
            fill={PLANNER_PARKING_FILL_COLOR}
            fillOpacity={PLANNER_PARKING_FILL_OPACITY}
            stroke={PLANNER_PARKING_LINE_COLOR}
          />
          <Text size="sm">{t('helpParkingAreas')}</Text>
        </LegendRow>
      </Stack>

      <Stack gap={6}>
        <Title order={5}>{t('helpNodesTitle')}</Title>
        <LegendRow>
          <NodeSwatch color={PLANNER_START_NODE_COLOR} label="1" />
          <Text size="sm">{t('helpNodeStart')}</Text>
        </LegendRow>
        <LegendRow>
          <FinishNodeSwatch />
          <Text size="sm">{t('helpNodeLast')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#40c057" label="3" />
          <Text size="sm">{t('helpNodeVisited')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#74c0fc" label="2" />
          <Text size="sm">{t('helpNodeEditable')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#748ffc" />
          <Text size="sm">{t('helpNodeAddTarget')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#fcc419" />
          <Text size="sm">{t('helpNodeHint')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#fd7e14" />
          <Text size="sm">{t('helpNodeGap')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color="#fa5252" />
          <Text size="sm">{t('helpNodePendingRemoval')}</Text>
        </LegendRow>
        <LegendRow>
          <NodeSwatch color={PLANNER_NON_ROUTABLE_NODE_COLOR} opacity={PLANNER_NON_ROUTABLE_NODE_OPACITY} />
          <Text size="sm">{t('helpNodeNonRoutable')}</Text>
        </LegendRow>
      </Stack>

      <Stack gap={6}>
        <Title order={5}>{t('helpAddingTitle')}</Title>
        <Text size="sm">{t('helpAddingStep1')}</Text>
        <Text size="sm">{t('helpAddingStep2')}</Text>
        <Text size="sm">{t('helpAddingStep3')}</Text>
        <Text size="sm">{t('helpAddingStep4')}</Text>
      </Stack>

      <Stack gap={6}>
        <Title order={5}>{t('helpEditingTitle')}</Title>
        <Text size="sm">{t('helpEditingStep1')}</Text>
        <Text size="sm">{t('helpEditingStep2')}</Text>
        <Text size="sm">{t('helpEditingStep3')}</Text>
      </Stack>

      <Stack gap={6}>
        <Title order={5}>{t('helpRemovingTitle')}</Title>
        <Text size="sm">{t('helpRemovingStep1')}</Text>
        <Text size="sm">{t('helpRemovingStep2')}</Text>
        <Text size="sm">{t('helpRemovingStep3')}</Text>
      </Stack>

      <Stack gap={6}>
        <Title order={5}>{t('helpShortcutsTitle')}</Title>
        <LegendRow>
          <Kbd>Backspace</Kbd>
          <Text size="sm">{t('helpBackspace')}</Text>
        </LegendRow>
        <LegendRow>
          <Kbd>Enter</Kbd>
          <Text size="sm">{t('helpEnter')}</Text>
        </LegendRow>
        <LegendRow>
          <Kbd>Esc</Kbd>
          <Text size="sm">{t('helpEscape')}</Text>
        </LegendRow>
        <Text size="sm">{t('helpSaving')}</Text>
        <Text size="sm">{t('helpZoom')}</Text>
        <Text size="sm">{t('helpSignPosts')}</Text>
        <Text size="sm">{t('helpParkingAreasToggle')}</Text>
      </Stack>
    </Stack>
  )
}

export default RoutePlannerHelp
